import './cutting-teach.css';

import { IonIcon, IonLabel, IonListHeader } from '@ionic/react';
import { Form } from 'formik';
import { closeOutline, cloudUploadOutline, trashBin } from 'ionicons/icons';
import { orderBy } from 'lodash';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import yup from 'yup';
import { RightAlignedButtons } from '../../../../../../components/button/aligned-buttons';
import { BaseButton } from '../../../../../../components/button/base-button';
import { DeleteButton } from '../../../../../../components/button/delete-button';
import { FormSubmitButton } from '../../../../../../components/form/form-submit.button';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { FlowItemFormComponent } from '../../../../shared/component/flow-item/edit/flow-item-form';
import { useAdditionalTeachOptions } from '../../../../shared/component/teach/teach.hooks';
import {
  TeachDetailProps,
  TeachOptionProps,
} from '../../../../shared/component/teach/teach.types';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import {
  WaypointCaptureRef,
  WaypointFormCapture,
} from '../../../../shared/hook/waypoint-capture.hooks';
import {
  FlowItem,
  FlowItemForm,
  FlowItemRef,
} from '../../../../shared/model/flow.types';
import { BeaconCuttingData } from '../../model/beacon-cutting-data';
import { getCuttingParameterMetadataFromForm } from '../../util/beacon-cutting.util';
import { CuttingTeachOption } from './cutting-teach-option';

type ItemRefToItemMap = Record<FlowItemRef, FlowItem>;

export type TeachDetailOption = Partial<TeachOptionProps> &
  Pick<TeachOptionProps, 'onAdd' | 'optionId'> & { className?: string };

const CuttingTeachDetail = (props: TeachDetailProps): ReactElement => {
  const { hasUnsavedItems, onAdd, onDiscard, onTeachError } = props;
  // This allows us to preserve previous form values while switching item types
  const previousFormItems = useRef<ItemRefToItemMap>({} as ItemRefToItemMap);

  const waypointCaptureRef = useRef<WaypointCaptureRef>();

  const [selectedItemRef, setSelectedItemRef] = useState<FlowItemRef>(
    props.initialItemRef
  );
  const [selectedItemIdx, setSelectedItemIdx] = useState<number>(0);
  const [saving, setSaving] = useState<boolean>(false);

  const onSave = async () => {
    setSaving(true);
    await props.onSave();
    setSaving(false);
  };

  const { flow, flowItemService, system } = useFlowServices();

  const availableFlowItems = useMemo(() => {
    return orderBy(
      flowItemService.getItemsMetadata().filter((i) => !i.hidden),
      ['displayOrder'],
      ['asc']
    );
  }, [flowItemService]);

  const onSubmit = useCallback(
    async (item: FlowItem) => {
      item.name = flowItemService.generateFlowItemName(item.ref);
      await onAdd([item]);
    },
    [flowItemService, onAdd]
  );

  const item = useMemo(() => {
    return (
      previousFormItems?.current[selectedItemRef] ??
      flowItemService.getDefaultItemForRef(selectedItemRef) ??
      {}
    );
  }, [selectedItemRef, flowItemService]);

  const onAddItemRef = useCallback(
    (ref: FlowItemRef) => {
      if (
        ref === BeaconCuttingData.FlowItemRef.MOVE ||
        ref === BeaconCuttingData.FlowItemRef.CUT
      ) {
        waypointCaptureRef.current?.forcePress();
      } else {
        onSubmit(item);
      }
    },
    [item, onSubmit]
  );

  const doneTeaching = () => {
    // TODO
  };

  const defaultOpts: TeachDetailOption[] = useMemo(
    () =>
      availableFlowItems.map((flowItemMeta) => {
        return {
          optionId: flowItemMeta.ref,
          className:
            flowItemMeta.ref === BeaconCuttingData.FlowItemRef.CUT
              ? 'cut-type'
              : undefined,
          icon: flowItemMeta.icon,
          description: flowItemMeta.description,
          itemRef: flowItemMeta.ref,
          onAdd: (item?: FlowItem) => {
            if (item) {
              onSubmit(item);
            } else {
              onAddItemRef(flowItemMeta.ref);
            }
          },
          onSelect: () => {
            selectTeachType(flowItemMeta.ref);
          },
        };
      }),
    [availableFlowItems, onAddItemRef, onSubmit]
  );

  const additionalOpts = useAdditionalTeachOptions({
    onAdd,
  });

  const teachOpts: TeachDetailOption[] = useMemo(() => {
    return defaultOpts.concat(additionalOpts);
  }, [additionalOpts, defaultOpts]);

  const selectTeachType = (ref: FlowItemRef) => {
    setSelectedItemRef(ref);
  };

  const getItemRefIndex = useCallback(
    (itemRef: FlowItemRef) => {
      return teachOpts.findIndex((opt) => opt.itemRef === itemRef);
    },
    [teachOpts]
  );

  const discard = () => {
    onDiscard();
  };

  const validationSchema: yup.AnySchema | undefined = useMemo(() => {
    if (item?.ref === BeaconCuttingData.FlowItemRef.CUT) {
      const form = flowItemService.convertFlowItemToForm(item, system);

      const parameters = getCuttingParameterMetadataFromForm(
        flowItemService,
        form.parameters
      );

      return flowItemService.getYupSchemaForItem(
        item.ref,
        system,
        { flow, form },
        parameters
      );
    }
  }, [item, system, flowItemService, flow]);

  const setPreviousFormItem = useCallback(
    (values: FlowItemForm) => {
      previousFormItems.current[selectedItemRef] =
        flowItemService.convertFormToFlowItem(values, system);
    },
    [system, flowItemService, selectedItemRef]
  );

  return (
    <FlowItemFormComponent
      item={item}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      hasFlowChanges={hasUnsavedItems}
    >
      {({ values }) => {
        const onLongPress = () => {
          const nextItemRef =
            selectedItemRef === BeaconCuttingData.FlowItemRef.CUT
              ? BeaconCuttingData.FlowItemRef.MOVE
              : BeaconCuttingData.FlowItemRef.CUT;
          setPreviousFormItem(values);
          selectTeachType(nextItemRef);
          const itemIdx = getItemRefIndex(nextItemRef);
          setSelectedItemIdx(itemIdx);
        };

        return (
          <>
            <WaypointFormCapture
              innerRef={waypointCaptureRef}
              autoSubmitForm={true}
              onLongPress={onLongPress}
              onTeachError={onTeachError}
            />
            <Form>
              <div className={'flow-panel-container teach-panel'}>
                <IonListHeader>
                  {getLocalizedText('common:add_items')}
                </IonListHeader>
                <div className="header-divider" />
                <div className="panel-option-container">
                  {teachOpts.map((option, index) => {
                    return (
                      <div key={index} className={option.className}>
                        <CuttingTeachOption
                          optionId={option.optionId}
                          icon={option.icon}
                          itemRef={option.itemRef}
                          description={option.description}
                          isSelected={index === selectedItemIdx}
                          isOtherSelected={index !== selectedItemIdx}
                          onAdd={option.onAdd}
                          onSelect={() => {
                            setPreviousFormItem(values);
                            if (typeof option.onSelect === 'function') {
                              option.onSelect();
                            }
                            setSelectedItemIdx(index);
                          }}
                          onDone={doneTeaching}
                          chipLabel={option.chipLabel}
                          isDisabled={option.isDisabled}
                        />
                      </div>
                    );
                  })}
                </div>
                <RightAlignedButtons>
                  {!hasUnsavedItems && (
                    <BaseButton
                      buttonId="teach-close"
                      buttonProps={{
                        type: 'button',
                        fill: 'outline',
                        color: 'danger',
                        onClick: onDiscard,
                      }}
                    >
                      <IonIcon icon={closeOutline} slot="start" />
                      <IonLabel>{getLocalizedText('common:close')}</IonLabel>
                    </BaseButton>
                  )}
                  {hasUnsavedItems && (
                    <DeleteButton
                      onDelete={discard}
                      header={getLocalizedText('common:discard_confirmation')}
                      buttonProps={{
                        type: 'button',
                        disabled: !hasUnsavedItems,
                      }}
                    >
                      <IonIcon icon={trashBin} slot="start" />
                      <IonLabel>{getLocalizedText('common:discard')}</IonLabel>
                    </DeleteButton>
                  )}
                  <FormSubmitButton
                    buttonId="teach-save"
                    loading={saving}
                    placement="none"
                    buttonProps={{
                      type: 'button',
                      onClick: onSave,
                      disabled: !hasUnsavedItems || saving,
                    }}
                  >
                    <IonIcon icon={cloudUploadOutline} slot="start" />
                    <IonLabel>{getLocalizedText('common:save')}</IonLabel>
                  </FormSubmitButton>
                </RightAlignedButtons>
              </div>
            </Form>
          </>
        );
      }}
    </FlowItemFormComponent>
  );
};

export { CuttingTeachDetail };
